import { Fragment } from 'react';

import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import { type OutreachProgressWidgetModel } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { useDynamicWidgetPromptAction } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widgets_context';
import {
    OutreachCaregiverRow,
    OutreachProgressHeader,
    OutreachProgressRail,
    OutreachReadErrorFooter,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/outreach_progress_components';
import {
    TEMPLATE_CARD_CLASS,
    TEMPLATE_CARD_FOOTER_ACTIONS_CLASS,
    TEMPLATE_CARD_FOOTER_CLASS,
    TEMPLATE_OUTLINE_BUTTON_SM_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { useOutreachProgress } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/use_outreach_progress';
import { resolveOutreachAutoAssignFirstResponder } from '@apps/web/lib/outreach_utils';
import { type JoinedOutreachRead } from '@libraries/typescript/api_client';

// Say nothing until the outreach read lands; an empty configured set is a
// real answer ("hold for review"), a missing outreach is not.
function acceptPolicyLabel(
    outreach: JoinedOutreachRead | undefined,
): string | null {
    if (outreach === undefined) {
        return null;
    }
    return resolveOutreachAutoAssignFirstResponder(outreach)
        ? 'Accepts: assigns automatically'
        : 'Accepts: hold for review';
}

export function OutreachProgressWidget({
    model,
    onCaregiverClick,
}: {
    readonly model: OutreachProgressWidgetModel;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const progress = useOutreachProgress(model);
    const promptAction = useDynamicWidgetPromptAction();
    const outreachStatus = progress.outreach?.status;
    const canControlOutreach =
        promptAction !== null &&
        (outreachStatus === 'in_progress' ||
            outreachStatus === 'pending' ||
            outreachStatus === 'paused');
    const widgetIssues = model.issues.filter(
        (issue) => issue.scope === 'widget',
    );
    const contentIssues = model.issues.filter(
        (issue) => issue.scope !== 'widget',
    );

    return (
        <Fragment>
            {widgetIssues.map((issue) => (
                <DynamicWidgetIssueNotice
                    key={issue.id}
                    issue={issue}
                    metricTags={progress.metricTags}
                />
            ))}
            <div className={TEMPLATE_CARD_CLASS}>
                <OutreachProgressHeader
                    model={model}
                    clientNames={progress.clientNames}
                    status={progress.status}
                    acceptedCount={progress.acceptedCount}
                    isAgentAnalyzing={progress.isAgentAnalyzing}
                    isLoadingOutreach={progress.isLoadingOutreach}
                    responseCounts={progress.responseCounts}
                    rowCount={progress.rowCount}
                    queuedCaregiverCount={progress.queuedCaregiverCount}
                    outreachStatus={progress.outreach?.status}
                    assignedCaregiverNames={progress.assignedCaregiverNames}
                    cancellationReason={progress.outreach?.cancellation_reason}
                    creationFacts={progress.creationFacts}
                    contentIssues={contentIssues}
                    metricTags={progress.metricTags}
                    acceptPolicyLabel={acceptPolicyLabel(progress.outreach)}
                />
                <OutreachProgressRail
                    model={model}
                    done={progress.railDone}
                    total={progress.railTotal}
                />
                {progress.hasLiveOutreach &&
                progress.visibleRows.length === 0 ? (
                    <p className="text-muted-foreground px-3.5 py-3 text-xs">
                        No caregivers contacted yet
                        {progress.queuedCaregiverCount > 0
                            ? ` · ${progress.queuedCaregiverCount} queued`
                            : ''}
                    </p>
                ) : null}
                {progress.visibleRows.map((row) => (
                    <OutreachCaregiverRow
                        key={row.caregiverId}
                        model={model}
                        row={row}
                        timezone={progress.timezone}
                        now={progress.now}
                        onCaregiverClick={onCaregiverClick}
                        onAssignRequest={
                            promptAction !== null
                                ? (assignRow) =>
                                      void promptAction(
                                          `Assign ${assignRow.displayName} to this outreach's shift.`,
                                      )
                                : undefined
                        }
                    />
                ))}
                {canControlOutreach ? (
                    <div className={TEMPLATE_CARD_FOOTER_CLASS}>
                        <div className={TEMPLATE_CARD_FOOTER_ACTIONS_CLASS}>
                            <button
                                type="button"
                                className={TEMPLATE_OUTLINE_BUTTON_SM_CLASS}
                                onClick={() =>
                                    void promptAction(
                                        outreachStatus === 'paused'
                                            ? 'Resume this outreach.'
                                            : 'Pause this outreach.',
                                    )
                                }
                            >
                                {outreachStatus === 'paused'
                                    ? 'Resume'
                                    : 'Pause'}
                            </button>
                            <button
                                type="button"
                                className={TEMPLATE_OUTLINE_BUTTON_SM_CLASS}
                                onClick={() =>
                                    void promptAction(
                                        'Cancel this outreach — do not contact anyone else.',
                                    )
                                }
                            >
                                Cancel outreach
                            </button>
                            <button
                                type="button"
                                className={TEMPLATE_OUTLINE_BUTTON_SM_CLASS}
                                onClick={() =>
                                    void promptAction(
                                        'Add more caregivers to this outreach — show me who else matches.',
                                    )
                                }
                            >
                                Add caregivers…
                            </button>
                        </div>
                    </div>
                ) : null}
                <OutreachReadErrorFooter
                    footerNote={progress.footerNote}
                    readIssue={progress.readIssue}
                    metricTags={progress.metricTags}
                    onRetryRead={progress.retryRead}
                />
            </div>
        </Fragment>
    );
}
